/**
 * Centralised auth helpers.
 *
 * Consolidates the cookie/localStorage access that was previously scattered
 * across src/stores/Auth/apiFactory.js and src/domain/Auth/index.js.
 */
import cookie from 'react-cookies';
import httpClient from '../http/client';
import {
  buildStoredUserMeta,
  normalizeLoginPayload,
} from './normalizeLoginPayload';
import { REFRESH_COOKIE, TOKEN_COOKIE } from './cookies';
import {
  clearSession,
  persistRefreshExpiry,
  startSessionWatch,
} from './session';

const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL;

// ─── Token helpers ────────────────────────────────────────────────────────────

export const getToken = (): string | null => {
  try {
    return (cookie.load(TOKEN_COOKIE) as string) ?? null;
  } catch {
    return null;
  }
};

export const setToken = (token: string): void => {
  cookie.save(TOKEN_COOKIE, token, { path: '/' });
};

export const removeToken = (): void => {
  cookie.remove(TOKEN_COOKIE, { path: '/' });
};

export const getRefreshToken = (): string | null => {
  try {
    return (cookie.load(REFRESH_COOKIE) as string) ?? null;
  } catch {
    return null;
  }
};

export const setRefreshToken = (token: string): void => {
  cookie.save(REFRESH_COOKIE, token, { path: '/' });
};

export const removeRefreshToken = (): void => {
  cookie.remove(REFRESH_COOKIE, { path: '/' });
};

export const isAuthenticated = (): boolean => Boolean(getToken());

// ─── User / ACL helpers ───────────────────────────────────────────────────────

export interface AuthUser {
  user: string | null;
  acl: Record<string, string> | null;
  meta: Record<string, unknown> | null;
  scope: { scope: string } | null;
}

export const getStoredUser = (): AuthUser => {
  try {
    return {
      user: localStorage.getItem('current_user'),
      acl: JSON.parse(localStorage.getItem('current_user_acl') ?? 'null'),
      meta: JSON.parse(localStorage.getItem('current_user_meta') ?? 'null'),
      scope: JSON.parse(localStorage.getItem('current_scope') ?? 'null'),
    };
  } catch {
    return { user: null, acl: null, meta: null, scope: null };
  }
};

export const storeUser = (
  user: Omit<AuthUser, 'scope'> & {
    token: string;
    refreshToken?: string | null;
    refreshExpiresIn?: number | null;
  },
): void => {
  setToken(user.token);
  if (user.refreshToken) {
    setRefreshToken(user.refreshToken);
  }
  if (user.refreshExpiresIn) {
    persistRefreshExpiry(user.refreshExpiresIn);
  }
  localStorage.setItem('current_user', user.user ?? '');
  localStorage.setItem('current_user_acl', JSON.stringify(user.acl));
  localStorage.setItem('current_user_meta', JSON.stringify(user.meta));
  startSessionWatch();
};

export const clearStoredUser = (): void => {
  clearSession();
};

export const isAuthorized = (scope: string): boolean => {
  try {
    const { acl } = getStoredUser();
    if (!acl) return false;

    return (
      Object.keys(acl).includes('bn:global') ||
      Object.keys(acl).includes(`org:${scope}`)
    );
  } catch {
    return false;
  }
};

export const getScope = (): AuthUser['scope'] => {
  try {
    return JSON.parse(localStorage.getItem('current_scope') ?? 'null');
  } catch {
    return null;
  }
};

export const setScope = (scope: AuthUser['scope']): void => {
  localStorage.setItem('current_scope', JSON.stringify(scope));
};

// ─── API calls ────────────────────────────────────────────────────────────────

export interface LoginResult {
  success: boolean;
  token?: string;
  acl?: Record<string, string>;
  meta?: Record<string, unknown>;
  user?: string;
  error?: string;
}

export const login = async (
  username: string,
  password: string,
): Promise<LoginResult> => {
  try {
    const response = await httpClient.post(`${AUTH_API_URL}/login`, {
      username,
      password,
    });

    const normalized = normalizeLoginPayload(response.data);
    if (!normalized) {
      return { success: false, error: 'Credenciais inválidas.' };
    }

    const meta = buildStoredUserMeta(normalized);

    storeUser({
      token: normalized.accessToken,
      refreshToken: normalized.refreshToken,
      refreshExpiresIn: normalized.refreshExpiresIn,
      user: username,
      acl: (normalized.acl as Record<string, string> | null) ?? null,
      meta,
    });

    return {
      success: true,
      token: normalized.accessToken,
      acl: (normalized.acl as Record<string, string> | null) ?? undefined,
      meta,
      user: username,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Erro de autenticação.';
    return { success: false, error: msg };
  }
};

export const logout = (): void => {
  clearStoredUser();
};

export {
  normalizeLoginPayload,
  buildStoredUserMeta,
  resolveAuditActor,
  extractRefreshTokenId,
  extractRefreshExpiresIn,
} from './normalizeLoginPayload';
export { TOKEN_COOKIE, REFRESH_COOKIE } from './cookies';
export {
  canSwitchOperation,
  isGlobalAcl,
  loadOperationsList,
  persistOperationsList,
} from './operations';
export {
  expireSessionToLogin,
  expireSessionIfStale,
  clearSession,
  startSessionWatch,
  hasPersistedAuthState,
} from './session';
